import { useMutation } from '@tanstack/react-query';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { ROLE } from 'services/assist-api/entities/role/roles';
import type { AssistQuery } from 'services/assist-api/query';
import { AssistQueryClient, useQueryAssist } from 'services/assist-api/query-client';
import { prepareCustomerAreaUser } from './customers-areas-users.prepare';

export type CustomerAreaUserRole = { id: number; name: ROLE };

export type BaseCustomerAreaUser = API_JSON & {
  area: API_JSON & { area: API_JSON };
  user: API_JSON;
  roles?: CustomerAreaUserRole[];
};

const groups = ['customersareasusers-details'];
const include = 'roles,user,area';
/**
 * Fetch customers-areas-users
 * @param params can be used to customise/limit the customers-areas-users retrieved
 * @returns an array of customers-areas-users
 */
export const getCustomerAreaUsers: GetMany<BaseCustomerAreaUser> = async (params) =>
  (
    await ASSIST_API.get('customers-areas-users', {
      params: requestParams({ groups, include }, params)
    })
  ).data;

/**
 * Fetch customers-areas-user (singular)
 * @param params can be used to customise/limit the customers-areas-user retrieved
 * @returns a single customers-areas-user
 */
export const getCustomerAreaUser: GetOne<BaseCustomerAreaUser> = async (id, params) =>
  (
    await ASSIST_API.get(`customers-areas-users/${id}`, {
      params: requestParams({ groups, include }, params)
    })
  ).data;

/**
 * Hook implementation to fetch customers-areas-users
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns customers-areas-users matching request
 */
export const useCustomerAreaUsers: AssistQuery.UseMany<BaseCustomerAreaUser> = (params, options) =>
  useQueryAssist(['customers-areas-users', params], () => getCustomerAreaUsers(params), options);

/**
 * Given a customers-areas-user Id fetch and return the customers-areas-user
 *  - only runs once Id is provided
 *  - fetches the one customers-areas-user
 *  - caches data
 * @param id customers-areas-users id
 * @param params optional parameters to change requested data
 * @returns the matching customers-areas-user
 */
export const useCustomerAreaUser: AssistQuery.UseOne<BaseCustomerAreaUser> = (
  id,
  params,
  options
) =>
  useQueryAssist(
    ['customers-areas-users', { id, ...params }],
    () => getCustomerAreaUser(id, params),
    {
      ...options,
      enabled: !!id
    }
  );

type PostCustomerAreaUserVariables = {
  area: number;
  user: number;
};

/**
 * Creates methods to send POST requests to the api
 *
 * mutate call will require a user ID and area ID
 *
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const usePostCustomerAreaUser: AssistQuery.UseMutation<
  BaseCustomerAreaUser,
  PostCustomerAreaUserVariables
> = (options) =>
  useMutation(
    ({ area, user }: PostCustomerAreaUserVariables) =>
      ASSIST_API.post('customers-areas-users', prepareCustomerAreaUser(area, user), {
        params: requestParams({ groups, include })
      }),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['customers-areas-users']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

/**
 * Creates methods to send DELETE requests to the api
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const useDeleteCustomerAreaUser: AssistQuery.UseMutation<BaseCustomerAreaUser, number> = (
  options
) =>
  useMutation((id) => ASSIST_API.delete(`customers-areas-users/${id}`), {
    ...options,
    onSettled: (...args) => {
      AssistQueryClient.invalidateQueries(['customers-areas-users']);
      if (options?.onSettled) {
        options.onSettled(...args);
      }
    }
  });

type PatchCustomerAreaUserVariables = RecursivePartial<BaseCustomerAreaUser> &
  Pick<BaseCustomerAreaUser, 'id'> & { roleIds: number[] };

/**
 * Creates methods to send PATCH requests to the api
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const usePatchCustomerAreaUser: AssistQuery.UseMutation<
  BaseCustomerAreaUser,
  PatchCustomerAreaUserVariables
> = (options) =>
  useMutation(
    ({ id, area, user, roleIds }: PatchCustomerAreaUserVariables) =>
      ASSIST_API.patch(
        `customers-areas-users/${id}`,
        prepareCustomerAreaUser(area?.id, user?.id, roleIds),
        {
          params: requestParams({ groups, include })
        }
      ),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['customers-areas-users']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );
